/**
 * Admin client for digest endpoints exposed by the vault management API.
 * Endpoints live in: pricingsaas-valut/supabase/functions/management/routes/digests.ts
 */

import type {
  DigestConfigWithIncludes,
  ListConfigsParams,
  ListConfigsResponse,
  ListDigestsParams,
  ListDigestsResponse,
  ListRunsParams,
  ListRunsResponse,
  WatchlistDetail,
} from '@/types/digestAdmin';

const API_BASE_URL =
  import.meta.env.VITE_PRICINGSAAS_MANAGEMENT_API_URL ||
  'https://nyqqynbtcrqdmhezmhsp.supabase.co/functions/v1/management';
const API_KEY = import.meta.env.VITE_PRICINGSAAS_MANAGEMENT_API_KEY || '';

function authHeaders(): HeadersInit {
  return {
    'x-api-key': API_KEY,
    'Content-Type': 'application/json',
    Accept: 'application/json',
  };
}

async function getJson<T>(path: string, search?: URLSearchParams): Promise<T> {
  if (!API_KEY) {
    throw new Error('VITE_PRICINGSAAS_MANAGEMENT_API_KEY is not set');
  }
  const url = search && [...search.keys()].length
    ? `${API_BASE_URL}${path}?${search.toString()}`
    : `${API_BASE_URL}${path}`;

  const res = await fetch(url, { method: 'GET', headers: authHeaders() });
  if (!res.ok) {
    let detail = res.statusText;
    try {
      const body = await res.json();
      detail = body.error || body.message || JSON.stringify(body);
    } catch {
      /* keep statusText */
    }
    throw new Error(`${res.status} ${detail}`);
  }
  return (await res.json()) as T;
}

function paramsFrom(obj: Record<string, unknown>): URLSearchParams {
  const sp = new URLSearchParams();
  for (const [k, v] of Object.entries(obj)) {
    if (v === undefined || v === null || v === '') continue;
    sp.set(k, String(v));
  }
  return sp;
}

export async function listDigestConfigs(
  params: ListConfigsParams = {}
): Promise<ListConfigsResponse> {
  return getJson<ListConfigsResponse>('/digest-configs', paramsFrom(params));
}

export async function getDigestConfig(
  id: string,
  include: Array<'sources' | 'template' | 'sender'> = ['sources']
): Promise<{ data: DigestConfigWithIncludes }> {
  const sp = new URLSearchParams();
  if (include.length) sp.set('include', include.join(','));
  return getJson<{ data: DigestConfigWithIncludes }>(`/digest-configs/${id}`, sp);
}

export async function listDigestRuns(
  params: ListRunsParams = {}
): Promise<ListRunsResponse> {
  return getJson<ListRunsResponse>('/digest-runs', paramsFrom(params));
}

export async function listDigests(
  params: ListDigestsParams = {}
): Promise<ListDigestsResponse> {
  return getJson<ListDigestsResponse>('/digests', paramsFrom(params));
}

export async function getWatchlist(
  id: string,
  include: Array<'pages' | 'tags'> = ['pages']
): Promise<{ data: WatchlistDetail }> {
  const sp = new URLSearchParams();
  if (include.length) sp.set('include', include.join(','));
  return getJson<{ data: WatchlistDetail }>(`/watchlists/${id}`, sp);
}

export interface DiffSummary {
  id: string;
  slug: string | null;
  company_logo?: string | null;
  page_id?: string | null;
  period?: string | null;
  title?: string | null;
}

// Pulled from the management /diffs/:id endpoint. Response shape is large
// (includes events + annotations), but we only surface a tiny summary.
export async function getDiffSummary(id: string): Promise<DiffSummary | null> {
  const resp = await getJson<{ data: DiffSummary & Record<string, unknown> }>(
    `/diffs/${id}`
  );
  if (!resp?.data) return null;
  return {
    id: resp.data.id,
    slug: resp.data.slug ?? null,
    company_logo: (resp.data.company_logo as string | undefined) ?? null,
    page_id: (resp.data.page_id as string | undefined) ?? null,
    period: (resp.data.period as string | undefined) ?? null,
    title: (resp.data.title as string | undefined) ?? null,
  };
}
